/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import React from "react";
const WorkspacesComponent = React.lazy(() => import("../pages/Workspaces"));
const ExploreComponent = React.lazy(() => import("../pages/Explore"));

const PrimaryEventsComponent = React.lazy(
  () => import("../pages/PrimaryEvents"),
);

const PresetsComponent = React.lazy(() => import("../pages/Presets"));
const VenuesComponent = React.lazy(() => import("../pages/Venues"));
const SessionsComponent = React.lazy(() => import("../pages/Sessions"));
const PackagesComponent = React.lazy(() => import("../pages/Packages"));
const RolesComponent = React.lazy(() => import("../pages/Roles"));
const UsersComponent = React.lazy(() => import("../pages/Users"));
const ContactsComponent = React.lazy(() => import("../pages/Contacts"));
const StaffComponent = React.lazy(() => import("../pages/Staff"));
const QuotaComponent = React.lazy(() => import("../pages/Quota"));
const DiscoverComponent = React.lazy(() => import("../pages/Discover"));
const AdminsComponent = React.lazy(() => import("../pages/Admins"));
const SendUpdatesComponent = React.lazy(() => import("../pages/SendUpdates"));
// const SettingsComponent = React.lazy(() => import("../pages/Settings"));
const StayComponent = React.lazy(() => import("../pages/Stay"));
const RoomTypeComponent = React.lazy(() => import("../pages/RoomType"));
const TicketTypeComponent = React.lazy(() => import("../pages/TicketType"));
const ChatsComponent = React.lazy(() => import("../pages/Chats"));
const ReservationComponent = React.lazy(() => import("../pages/Reservation"));

const NotificationsComponent = React.lazy(
  () => import("../pages/Notifications"),
);

const UsersReportComponent = React.lazy(() => import("../pages/Reports/Users"));
const JourneysComponent = React.lazy(() => import("../pages/Journeys"));
const DriversComponent = React.lazy(() => import("../pages/Drivers"));
const VehiclesComponent = React.lazy(() => import("../pages/Vehicles"));
const EventInfoComponent = React.lazy(() => import("../pages/EventInfo"));
const LiveTrackingComponent = React.lazy(() => import("../pages/LiveTracking"));
const ApplicationsComponent = React.lazy(() => import("../pages/Applications"));
const ConvoyComponent = React.lazy(() => import("../pages/Convoy"));

const ApplicationFormsComponent = React.lazy(
  () => import("../pages/ApplicationForms"),
);

const EventReviewComponent = React.lazy(() => import("../pages/EventReview"));

const DedicatedVehiclesComponent = React.lazy(
  () => import("../pages/DedicatedVehicles"),
);

const RemindersComponent = React.lazy(() => import("../pages/Reminders"));
const EventAddOnsComponent = React.lazy(() => import("../pages/EventAddOns"));

const AdministrationComponent = React.lazy(
  () => import("../pages/Administration"),
);

const DirectoryComponent = React.lazy(() => import("../pages/Directory"));

const MobileActionsComponent = React.lazy(
  () => import("../pages/MobileActions"),
);

const StoreSettingsComponent = React.lazy(
  () => import("../pages/StoreSettings"),
);

const BadgeTypesComponent = React.lazy(() => import("../pages/BadgeTypes"));
const BadgeGroupsComponent = React.lazy(() => import("../pages/BadgeGroups"));
const OrdersComponent = React.lazy(() => import("../pages/Orders"));

const RefundReasonsComponent = React.lazy(
  () => import("../pages/Refund/RefundReasons"),
);

const PaymentMethodsComponent = React.lazy(
  () => import("../pages/PaymentMethods"),
);

const CouponCategoriesComponent = React.lazy(
  () => import("../pages/CouponCategories"),
);

const CouponsComponent = React.lazy(() => import("../pages/Coupons"));

const ReturnReasonsComponent = React.lazy(
  () => import("../pages/ReturnReasons"),
);

const AffiliateLinksComponent = React.lazy(
  () => import("../pages/Affiliate/AffiliateLinks"),
);

const InviteesComponent = React.lazy(() => import("../pages/Invitees"));

const AdmissionSettingsComponent = React.lazy(
  () => import("../pages/AdmissionSettings"),
);

const StaffRolesComponent = React.lazy(() => import("../pages/StaffRoles"));
const ItineraryComponent = React.lazy(() => import("../pages/Itinerary"));
const AnalyticsComponent = React.lazy(() => import("../pages/Analytics"));

const AppConfigComponent = React.lazy(
  () => import("../pages/AppConfig/AppConfig"),
);

const ThemingComponent = React.lazy(() => import("../pages/Theming"));
const AccessConfigComponent = React.lazy(() => import("../pages/AccessConfig"));
const BadgeMetrics = React.lazy(() => import("../pages/BadgeMetrics/index"));

const OrderMetrics = React.lazy(
  () => import("../pages/OrderMetrics/OrderMetrics"),
);

const WebPages = React.lazy(() => import("../pages/WebPages/WebPageRoutes"));

/**
 * @description
 * To add a new route object to the `ROUTES_LIST`, follow these steps:
 *
 * 1. **Define the Key**:
 *    - The key of the route object in `ROUTES_LIST` is considered as the key in the `roleContext` and the `permissionKey` in the sidebar.
 *
 * 2. **Backend and Frontend Page Names**:
 *    - Add the `backendPageNames` and `frontendPageNames` properties.
 *    - These keys are used by the `routeUtils` function to map the `route` property (number 3) for frontend routing and the base API endpoint.
 *    - If the backend API permission key differs from the frontend page key, update the `page` property in the `options` object of the API function. Refer to the API files for more details.
 *
 * 3. **Route Property**:
 *    - The `route` property is used for frontend routing and as the base API endpoint.
 *
 * 4. **Route Map**:
 *    - Use the `routeMap` property for nested routing and for navigation.
 *    - The `routeMap.base` property is used for slug in Sidebar.
 *
 * 5. **Component Property**:
 *    - The `component` property should point to the component used for navigation.
 *
 * Example:
 * ```typescript
 * NEW_ROUTE: {
 *   backendPageNames: ["new_backend_key"],
 *   frontendPageNames: ["new_frontend_key"],
 *   component: "NewComponent",
 *   route: "/new_route",
 *   sidebarId: "",
 *   routeMap: {
 *     base: "/new_route",
 *     nested: "/new_route/nested",
 *   },
 * },
 * ```
 */

import { RoutesListType } from "./types";
import { IMPORT_TYPE } from "components/common/ImportModal/constants";

// Define routes with lazy-loaded components
const routesList: RoutesListType = {
  EXPLORE: {
    backendPageNames: ["explore"],
    frontendPageNames: ["explore"],
    component: ExploreComponent,
    route: "/explore",
    sidebarId: "",
    routeMap: {
      base: "/explore",
      pagination: (page: number = 1) => `/explore/p/${page}`,
      edit: (id: string) => `/explore/${id}`,
    },
  },
  EVENTS: {
    backendPageNames: ["itinerary", "events", "primary_events", "event_info"],
    frontendPageNames: ["events", "primary_events"],
    component: PrimaryEventsComponent,
    route: "/events",
    sidebarId: "",
    routeMap: {
      base: "/events",
      pagination: (page: number = 1) => `/events/p/${page}`,
      edit: (id: string) => `/events/${id}`,
      new: "/events/new",
      options: "/events/options",
      create: (type: string, copiedEventId?: string) =>
        `/events/create/${type}${copiedEventId ? `/ ${copiedEventId}` : ""}`,
    },
  },
  PRESETS: {
    backendPageNames: ["presets"],
    frontendPageNames: ["presets"],
    component: PresetsComponent,
    route: "/presets",
    sidebarId: "",
    routeMap: {
      base: "/presets",
      event: {
        base: "/presets/events",
        new: "/presets/events/new",
        edit: (id: string) => `/presets/events/edit/${id}`,
        preview: (id: string) => `/presets/events/preview/${id}`,
      },
      airport: {
        base: "/presets/airports",
        pagination: (page: number = 1) => `/presets/airports/p/${page}`,
        new: "/presets/airports/new",
        edit: (id: string) => `/presets/airports/edit/${id}`,
        preview: (id: string) => `/presets/airports/preview/${id}`,
      },
      admins: {
        base: "/presets/admins",
        pagination: (page: number = 1) => `/presets/admins/p/${page}`,
        new: "/presets/admins/new",
        edit: (id: string) => `/presets/admins/${id}`,
      },
      roles: {
        base: "/presets/roles",
        new: "/presets/roles/new",
        edit: (id: string) => `/presets/roles/${id}`,
        pagination: (page: number = 1) => `/presets/roles/p/${page}`,
      },
      reminders: {
        key: "presets",
        base: "/presets/reminders",
        pagination: (page: number = 1) => `/presets/reminders/p/${page}`,
        emails: "/presets/reminders/emails",
        emailEdit: (id: string) => `/presets/reminders/emails/${id}`,
        emailStructure: "/presets/reminders/email_structure",
        emailsPagination: (page: number = 1) =>
          `/presets/reminders/emails/p/${page}`,
        editEmailStructure: (id: string) =>
          `/presets/reminders/email_structure/${id}`,
        editEmailBuilder: (id: string) =>
          `/presets/reminders/email_builder/${id}`,
        emailStructurePagination: (page: number = 1) =>
          `/presets/reminders/email_structure/p/${page}`,
        newEmailStructure: (page: number = 1) =>
          `/presets/reminders/email_structure/new/p/${page}`,
        notifications: "/presets/reminders/notifications",
        notificationEdit: (id: string) =>
          `/presets/reminders/notifications/${id}`,
        sms: "/presets/reminders/sms",
        smsEdit: (id: string) => `/presets/reminders/sms/${id}`,
        smsPagination: (page: number = 1) => `/presets/reminders/sms/p/${page}`,
      },
      theming: {
        base: "/presets/theming",
      },
      classifications: {
        base: "/presets/classifications",
      },
    },
  },
  VENUES: {
    backendPageNames: ["venues", "stadiums"],
    frontendPageNames: ["venues"],
    component: VenuesComponent,
    route: "/venues",
    sidebarId: "",
    routeMap: {
      base: "/venues",
      pagination: (page: number = 1) => `/venues/p/${page}`,
      venue: (id: number, page: number = 1) => `/venues/${id}/p/${page}`,
      new: "/venues/new",
      edit: (id: number) => `/venues/edit/${id}`,
    },
  },
  SESSIONS: {
    backendPageNames: ["sessions", "matches"],
    frontendPageNames: ["sessions", "matches", "session"],
    component: SessionsComponent,
    route: "/sessions",
    sidebarId: "",
    routeMap: {
      base: "/sessions",
      pagination: (page: number = 1) => `/sessions/p/${page}`,
      analytics: {
        base: `/sessions/analytics`,
      },
      filtered: (page: number = 1) => `/sessions/filtered/p/${page}`,
      new: "/sessions/new",
      edit: (id: number) => `/sessions/${id}/edit`,
      preview: (id: number) => `/sessions/${id}/preview`,
      search: (search: string, page?: number) =>
        `/sessions/search${page ? `/p/${page || 1}` : ""}?q=${search}`,
    },
  },
  PACKAGES: {
    backendPageNames: ["packages"],
    frontendPageNames: ["packages"],
    component: PackagesComponent,
    route: "/packages",
    sidebarId: "",
    routeMap: {
      base: "/packages",
    },
  },
  ROLES: {
    backendPageNames: ["roles"],
    frontendPageNames: ["roles"],
    component: RolesComponent,
    route: "/roles",
    sidebarId: "",
    routeMap: {
      base: "/roles",
      new: "/roles/new",
      edit: (id: number) => `/roles/${id}`,
    },
  },
  USERS: {
    backendPageNames: ["users", "attendees"],
    frontendPageNames: ["users", "attendees"],
    component: UsersComponent,
    route: "/users",
    sidebarId: "",
    routeMap: {
      base: "/users",
      statistics: (target: string, type: string, page: number = 1) =>
        `/users/${target}/stats/${type}/p/${page}`,
      downloadStats: (page: number = 1, target: string) =>
        `/users/attendees/${target}/stats/p/${page}`,
      groups: (page: number = 1) => `/users/groups/p/${page}`,
      allUsers: (page: number = 1) => `/users/users/p/${page}`,
      group: (id: number, page: number = 1) => `/users/group/${id}/p/${page}`,
      attendees: (page: number = 1) => `/users/attendees/p/${page}`,
      registrationGroup: (page: number = 1) =>
        `/users/registration-group/p/${page}`,
      search: "/users/search",
      edit: (id: number, params?: string) =>
        `/users/${id}${params ? `?${params}` : ""}`,
      new: "/users/new",
      tripDetails: {
        new: (id: number) => `/users/${id}/trip/new`,
        edit: (id: number, tripId: number) =>
          `/users/${id}/trip/${tripId}/edit`,
        view: (id: number, tripId: number) => `/users/${id}/trip/${tripId}`,
      },
      tripFlights: {
        list: (id: number, tripId: number) =>
          `/users/${id}/trip/${tripId}/flights`,
        new: (id: number) => `/users/${id}/trip/flights/new`,
        edit: (id: number, tripId: number, flightId: number) =>
          `/users/${id}/trip/${tripId}/flights/${flightId}`,
      },
      generalFlights: {
        list: (id: number) => `/users/${id}/general/flights`,
        edit: (id: number, flightId: number) =>
          `/users/${id}/general/flights/${flightId}`,
      },
      generalStays: {
        list: (id: number) => `/users/${id}/general/stays`,
        edit: (id: number, stayId: number) =>
          `/users/${id}/general/stays/${stayId}`,
      },
      tripStays: {
        list: (id: number, tripId: number) =>
          `/users/${id}/trip/${tripId}/stays`,
        new: (id: number) => `/users/${id}/trip/stays/new`,
        edit: (id: number, tripId: number, stayId: number) =>
          `/users/${id}/trip/${tripId}/stays/${stayId}`,
      },
      tripSessions: {
        list: (id: number, tripId: number) =>
          `/users/${id}/trip/${tripId}/sessions`,
        new: (id: number, tripId: number, page: number = 1) =>
          `/users/${id}/trip/${tripId}/sessions/new/p/${page}`,
        tickets: (
          id: number,
          tripId: number,
          sessionId: number,
          page: number = 1,
        ) =>
          `/users/${id}/trip/${tripId}/sessions/${sessionId}/tickets/p/${page}`,
        edit: (id: number, tripId: number, sessionId: number) =>
          `/users/${id}/trip/${tripId}/sessions/${sessionId}`,
      },
      generalSessions: {
        list: (id: number) => `/users/${id}/general/sessions`,
        edit: (id: number, sessionId: number) =>
          `/users/${id}/sessions/general/${sessionId}`,
        new: (id: number, page: number = 1) =>
          `/users/${id}/general/sessions/new/p/${page}`,
        tickets: (id: number, sessionId: number, page: number = 1) =>
          `/users/${id}/general/sessions/${sessionId}/tickets/p/${page}`,
      },
      invitation: (id: number) => `/users/${id}/invitation`,
      companions: {
        pagination: (id: number, page: number = 1) =>
          `/users/${id}/companions/p/${page}`,
        edit: (userId: number, companionId: number) =>
          `/users/${userId}/companion/${companionId}`,
      },
    },
  },
  CONTACTS: {
    backendPageNames: ["contacts"],
    frontendPageNames: ["contacts"],
    component: ContactsComponent,
    route: "/contacts",
    sidebarId: "",
    routeMap: {
      base: "/contacts",
      new: "/contacts/poc/new",
      user: (id: number) => `/contacts/poc/${id}`,
    },
  },
  STAFF: {
    backendPageNames: ["staff", "all_staff"],
    frontendPageNames: ["staff", "all_staff"],
    component: StaffComponent,
    route: "/staff",
    sidebarId: "",
    routeMap: {
      base: "/staff",
      groups: (page: number = 1) => `/staff/groups/p/${page}`,
      group: (id: number, page: number = 1) => `/staff/group/${id}/p/${page}`,
      allStaff: (page: number = 1) => `/staff/p/${page}`,
      new: "/staff/new",
      edit: (hash_id: string) => `/staff/${hash_id}`,
    },
  },
  QUOTA: {
    backendPageNames: ["quota", "quotas"],
    frontendPageNames: ["quota", "quotas"],
    component: QuotaComponent,
    route: "/quota",
    sidebarId: "",
    routeMap: {
      base: "/quota",
    },
  },
  DISCOVER: {
    backendPageNames: ["discover"],
    frontendPageNames: ["discover"],
    component: DiscoverComponent,
    route: "/discover",
    sidebarId: "",
    routeMap: {
      base: "/discover",
    },
  },
  ADMINS: {
    backendPageNames: ["admins"],
    frontendPageNames: ["admins"],
    component: AdminsComponent,
    route: "/admins",
    sidebarId: "",
    routeMap: {
      base: "/admins",
      new: "/admins/new",
      edit: (id: number) => `/admins/${id}`,
    },
  },
  UPDATES: {
    backendPageNames: [],
    frontendPageNames: ["notification", "updates"],
    component: SendUpdatesComponent,
    route: "/updates",
    sidebarId: "",
    routeMap: {
      base: "/updates",
      new: "/updates/new",
      edit: (id: number) => `/updates/edit/${id}`,
      view: (id: number) => `/updates/view/${id}`,
      pagination: (page: number) => `/updates/p/${page}`,
      search: (search: string, page?: number) =>
        `/updates/search${page ? `/p/${page || 1}` : ""}?q=${search}`,
    },
  },
  // SETTINGS: {
  //   backendPageNames: ["settings"],
  //   frontendPageNames: ["settings"],
  //   component: SettingsComponent,
  //   route: "/settings",
  //   sidebarId: "",
  //   routeMap: {
  //     base: "/settings",
  //   },
  // },
  STAYS: {
    backendPageNames: ["stay", "stays"],
    frontendPageNames: ["stay", "stays"],
    component: StayComponent,
    route: "/stays",
    sidebarId: "",
    routeMap: {
      base: "/stays",
      allPagination: (page: number) => `/stays/all/p/${page}`,
      stayPagination: (id: number, page = 1) => `/stays/stay/${id}/p/${page}`,
      new: {
        room: "/stays/room/new",
        stay: "/stays/new",
      },
      edit: {
        stay: (id: number) => `/stays/${id}`,
        room: (id: number) => `/stays/room/${id}`,
      },
    },
  },
  ROOMS: {
    backendPageNames: [],
    frontendPageNames: [
      "rooms",
      "rooms-types",
      "room_types",
      "room_type",
      "room-types",
      "room types",
    ],
    component: RoomTypeComponent,
    route: "/rooms",
    sidebarId: "",
    routeMap: {
      base: "/rooms",
      new: "/rooms/new",
      stats: "/rooms/statistics",
      edit: (id: number) => `/rooms/${id}`,
      pagination: (page: number = 1) => `/rooms/p/${page}`,
    },
  },
  TICKETS: {
    backendPageNames: [],
    frontendPageNames: ["tickets", "ticket types"],
    component: TicketTypeComponent,
    route: "/tickets",
    sidebarId: "",
    routeMap: {
      base: "/tickets",
      new: "/tickets/new",
      edit: (id: number) => `/tickets/${id}`,
      pagination: (page: number = 1) => `/tickets/p/${page}`,
    },
  },
  CHATS: {
    backendPageNames: [],
    frontendPageNames: ["chats", "chat-groups", "chats-groups"],
    component: ChatsComponent,
    route: "/chats",
    sidebarId: "",
    routeMap: {
      base: "/chats",
      new: "/chats/new",
      edit: (id: number) => `/chats/${id}`,
      pagination: (page: number = 1) => `/chats/p/${page}`,
    },
  },
  RESERVATIONS: {
    backendPageNames: ["reservation"],
    frontendPageNames: ["reservation", "reservations"],
    component: ReservationComponent,
    route: "/reservations",
    sidebarId: "",
    routeMap: {
      base: "/reservations",
      pagination: (page: number = 1) => `/reservations/p/${page}`,
      overview: (page: number = 1) => `/reservations/overview/p/${page}`,
      new: "/reservations/new",
      edit: (id: number) => `/reservations/${id}`,
    },
  },
  NOTIFICATIONS: {
    backendPageNames: [],
    frontendPageNames: ["notifications", "alerts"],
    component: NotificationsComponent,
    route: "/notifications",
    sidebarId: "",
    routeMap: {
      base: "/notifications",
    },
  },
  USER_REPORTS: {
    backendPageNames: [],
    frontendPageNames: ["user_reports"],
    component: UsersReportComponent,
    route: "/user_reports",
    sidebarId: "",
    routeMap: {
      base: "/user_reports",
      basePagination: (page: number = 1) => `/user_reports/p/${page}`,
      report: (id: number) => `/user_reports/user/${id}`,
    },
  },
  STATISTICS: {
    backendPageNames: ["statistics"],
    frontendPageNames: ["statistics", "event_statistics", "event-statistics"],
    component: null,
    route: "statistics",
    sidebarId: "",
    routeMap: {
      base: "statistics",
    },
  },
  JOURNEYS: {
    backendPageNames: ["rides", "journeys"],
    frontendPageNames: ["rides", "journeys"],
    component: JourneysComponent,
    route: "/journeys",
    sidebarId: "",
    routeMap: {
      base: "/journeys",
      pagination: (page: number = 1) => `/journeys/p/${page}`,
      new: "/journeys/new",
      edit: (id: number) => `/journeys/${id}`,
      ride: {
        new: (journeyId: number) => `/journeys/${journeyId}/ride/new`,
        edit: (journeyId: number, rideId: number) =>
          `/journeys/${journeyId}/ride/${rideId}`,
      },
    },
  },
  DRIVERS: {
    backendPageNames: ["driver", "drivers"],
    frontendPageNames: ["driver", "drivers"],
    component: DriversComponent,
    route: "/drivers",
    sidebarId: "",
    routeMap: {
      base: "/drivers",
      new: "/drivers/new",
      edit: (id: string) => `/drivers/${id}`,
      pagination: (page: number = 1) => `/drivers/p/${page}`,
      downloadStats: (page: number = 1, target: string) =>
        `/drivers/${target}/stats/p/${page}`,
    },
  },
  VEHICLES: {
    backendPageNames: ["vehicle", "vehicles"],
    frontendPageNames: ["vehicle", "vehicles"],
    component: VehiclesComponent,
    route: "/vehicles",
    sidebarId: "",
    routeMap: {
      base: "/vehicles",
      new: "/vehicles/new",
      edit: (id: string) => `/vehicles/v/${id}`,
      duplicate: (id: string) => `/vehicles/duplicate/${id}`,
      category: (id: string) => `/vehicles/${id}`,
      categoryPagination: (id: string, page = 1) =>
        `/vehicles/category/${id}/p/${page}`,
      pagination: (page: number = 1) => `/vehicles/p/${page}`,
      stats: (type: string) => `/vehicles/statistics/${type}`,
    },
  },
  EVENT_INFO: {
    backendPageNames: ["event_info"],
    frontendPageNames: ["event_info"],
    component: EventInfoComponent,
    route: "/event_info",
    sidebarId: "",
    routeMap: {
      base: "/event_info",
    },
  },
  LIVE_TRACKING: {
    backendPageNames: [],
    frontendPageNames: [
      "tracking",
      "live-tracking",
      "live_tracking",
      "live tracking",
    ],
    component: LiveTrackingComponent,
    route: "/tracking",
    sidebarId: "",
    routeMap: {
      base: "/tracking",
    },
  },
  APPLICATIONS: {
    backendPageNames: ["applications"],
    frontendPageNames: ["applications"],
    component: ApplicationsComponent,
    route: "/applications",
    sidebarId: "",
    routeMap: {
      base: "/applications",
      edit: (applicationId: string, type: string = "user") =>
        `/applications/${type}/${applicationId}`,
      pagination: (page: number = 1) => `/applications/p/${page}`,
    },
  },
  CONVOYS: {
    backendPageNames: ["convoy", "convoys"],
    frontendPageNames: ["convoy", "convoys"],
    component: ConvoyComponent,
    route: "/convoys",
    sidebarId: "",
    routeMap: {
      base: "/convoys",
      new: "/convoys/new",
      edit: (id: number) => `/convoys/edit/${id}`,
      pagination: (page: number = 1) => `/convoys/p/${page}`,
      newRide: (id: number) => `/convoys/${id}/rides/new`,
      editRide: (id: number, rideId: number) =>
        `/convoys/${id}/rides/${rideId}`,
      details: (id: number) => `/convoys/${id}`,
    },
  },
  APPLICATION_FORMS: {
    backendPageNames: ["application_forms", "application forms"],
    frontendPageNames: ["application_forms", "application forms"],
    component: ApplicationFormsComponent,
    route: "/application_forms",
    sidebarId: "",
    routeMap: {
      base: "/application_forms",
      new: "/application_forms/new",
      pagination: (page: number = 1) => `/application_forms/p/${page}`,
      edit: (formType: string, id: string) =>
        `/application_forms/edit/${formType}/${id}`,
      dynamicFields: `/application_forms/dynamic-fields`,
    },
  },
  EVENT_REVIEW: {
    backendPageNames: ["event_review"],
    frontendPageNames: ["event_review"],
    component: EventReviewComponent,
    route: "/event_review",
    sidebarId: "",
    routeMap: {
      base: "/event_review",
      pagination: (page: number = 1) => `/event_review/p/${page}`,
    },
  },
  DEDICATED: {
    backendPageNames: [],
    frontendPageNames: ["dedicated", "dedicated vehicles"],
    component: DedicatedVehiclesComponent,
    route: "/dedicated",
    sidebarId: "",
    routeMap: {
      base: "/dedicated",
      pagination: (page: number = 1) => `/dedicated/p/${page}`,
      new: "/dedicated/new",
      edit: (id: string) => `/dedicated/${id}`,
    },
  },
  EVENT_COPIES: {
    backendPageNames: [
      "event-copies",
      "event copies",
      "event_copies",
      "updates & reminders",
    ],
    frontendPageNames: ["event-copies", "event copies", "event_copies"],
    component: RemindersComponent,
    route: "/event_copies",
    sidebarId: "email_templates",
    routeMap: {
      key: "event_copies",
      base: "/event_copies",
      emails: "/event_copies/emails",
      emailsPagination: (page: number = 1) => `/event_copies/emails/p/${page}`,
      emailStructure: "/event_copies/email_structure",
      emailStructurePagination: (page: number = 1) =>
        `/event_copies/email_structure/p/${page}`,
      newEmailStructure: (page: number = 1) =>
        `/event_copies/email_structure/new/p/${page}`,
      notifications: "/event_copies/notifications",
      sms: "/event_copies/sms",
      smsPagination: (page: number = 1) => `/event_copies/sms/p/${page}`,
      emailEdit: (id: string) => `/event_copies/emails/${id}`,
      editEmailStructure: (id: string) => `/event_copies/email_structure/${id}`,
      editEmailBuilder: (id: string) => `/event_copies/email_builder/${id}`,
      notificationEdit: (id: string) => `/event_copies/notifications/${id}`,
      smsEdit: (id: string) => `/event_copies/sms/${id}`,
    },
  },
  EVENT_ADD_ONS: {
    backendPageNames: ["event-add-ons", "event add-ons", "event_add_ons"],
    frontendPageNames: ["event-add-ons", "event add-ons", "event_add_ons"],
    component: EventAddOnsComponent,
    route: "/event_add_ons",
    sidebarId: "",
    routeMap: {
      base: "/event_add_ons",
    },
  },
  WORKSPACES: {
    backendPageNames: ["workspaces"],
    frontendPageNames: ["workspaces"],
    component: WorkspacesComponent,
    route: "/workspaces",
    sidebarId: "",
    routeMap: {
      base: "/workspaces",
      pagination: (page: number = 1) => `/workspaces/p/${page}`,
      events: {
        events: (id: string, page: number = 1) =>
          `/workspaces/${id}/events/p/${page}`,
        pagination: (id: string, page: number = 1) =>
          `/workspaces/${id}/events/p/${page}`,
      },
      search: (search: string, page?: number) =>
        `/workspaces${page ? `/p/${page || 1}` : ""}?q=${search}`,
    },
  },
  ADMINISTRATION: {
    backendPageNames: ["administration"],
    frontendPageNames: ["administration"],
    component: AdministrationComponent,
    route: "/administration",
    sidebarId: "",
    routeMap: {
      base: "/administration",
      admins: {
        base: "/administration/admins",
        pagination: (page: number = 1) => `/administration/admins/p/${page}`,
        new: "/administration/admins/new",
        newAccessTab: (id: string) => `/administration/admins/new/${id}`,
        edit: (id: string) => `/administration/admins/edit/${id}`,
        search: (search: string, page?: number) =>
          `/administration/admins${page ? `/p/${page}` : ""}?q=${search}`,
      },
      teams: {
        base: "/administration/teams",
        pagination: (page: number = 1) => `/administration/teams/p/${page}`,
        team: (id: string) => `/administration/teams/${id}`,
        new: "/administration/teams/new",
      },
    },
  },
  DIRECTORY: {
    backendPageNames: ["directory"],
    frontendPageNames: ["directory"],
    component: DirectoryComponent,
    route: "/directory",
    sidebarId: "",
    routeMap: {
      base: "/directory",
      pagination: (page: number = 1) => `/directory/p/${page}`,
      new: "/directory/new",
      edit: (id: string) => `/directory/${id}`,
      search: (search: string, page?: number) =>
        `/directory/search${page ? `/p/${page || 1}` : ""}?q=${search}`,
    },
  },
  IN_APP_GUIDANCE: {
    backendPageNames: ["in-app-guidance", "in_app_guidance"],
    frontendPageNames: [
      "in-app-guidance",
      "in-app guidance",
      "in_app_guidance",
    ],
    component: MobileActionsComponent,
    route: "/in-app-guidance",
    sidebarId: "",
    routeMap: {
      base: "/in-app-guidance",
      pagination: (page: number = 1) => `/in-app-guidance/p/${page}`,
      new: "/in-app-guidance/new",
      edit: (id: string) => `/in-app-guidance/${id}`,
      search: (search: string, page?: number) =>
        `/in-app-guidance/search${page ? `/p/${page}` : ""}?q=${search}`,
    },
  },
  STORE_SETTINGS: {
    backendPageNames: ["stores", "store_settings", "store-settings"],
    frontendPageNames: ["stores", "store_settings", "store-settings"],
    component: StoreSettingsComponent,
    route: "/store_settings",
    sidebarId: "",
    routeMap: {
      base: "/store_settings",
      new: "/store_settings/new",
      edit: (id: string) => `/store_settings/${id}`,
      pagination: (page: number = 1) => `/store_settings/p/${page}`,
    },
  },
  BADGE_TYPES: {
    backendPageNames: ["badge_types", "badge-types"],
    frontendPageNames: ["badge_types", "badge-types"],
    component: BadgeTypesComponent,
    route: "/badge-types",
    sidebarId: "",
    routeMap: {
      base: "/badge-types",
      details: (id: string) => `/badge-types/${id}/details`,
      access: (id: string) => `/badge-types/${id}/access`,
      features: (id: string) => `/badge-types/${id}/features`,
      design: (id: string) => `/badge-types/${id}/design`,
      discounts: (id: string) => `/badge-types/${id}/discounts`,
      pagination: (page: number = 1) => `/badge-types/p/${page}`,
      assignForm: (id: string) => `/badge-types/${id}/assign-form`,
      linkedBadges: {
        base: (id: string) => `/badge-types/${id}/linked-badges`,
        pagination: (id: string, page: number = 1) =>
          `/badge-types/${id}/linked-badges/p/${page}`,
        new: (id: string) => `/badge-types/${id}/linked-badges/new`,
        edit: (id: string, linkedBadgeId: string) =>
          `/badge-types/${id}/linked-badges/${linkedBadgeId}`,
        access: (id: string, linkedBadgeId: string) =>
          `/badge-types/${id}/linked-badges/${linkedBadgeId}/access`,
      },
    },
  },
  BADGE_GROUPS: {
    backendPageNames: ["badge_groups", "badge-groups"],
    frontendPageNames: ["badge_groups", "badge-groups"],
    component: BadgeGroupsComponent,
    route: "/badge-groups",
    sidebarId: "",
    routeMap: {
      base: "/badge-groups",
      new: "/badge-groups/new",
      edit: (id: string) => `/badge-groups/${id}/edit`,
      pagination: (page: number = 1) => `/badge-groups/p/${page}`,
    },
  },
  ORDERS: {
    backendPageNames: ["orders"],
    frontendPageNames: ["orders"],
    component: OrdersComponent,
    route: "/orders",
    sidebarId: "",
    routeMap: {
      base: "/orders",
      pagination: (page: number = 1) => `/orders/p/${page}`,
      search: (search: string, page?: number) =>
        `/orders/search${page ? `/p/${page}` : ""}?q=${search}`,
      cart: (id: string) => `/orders/${id}/cart`,
      channel: (id: string) => `/orders/${id}/channel`,
      customer: (id: string) => `/orders/${id}/customer`,
      payments: {
        pagination: (id: string, page: number = 1) =>
          `/orders/${id}/payments/p/${page}`,
        new: (id: string) => `/orders/${id}/payments/new`,
      },
      badgesList: (id: string, page: number = 1) =>
        `/orders/${id}/badges-list/p/${page}`,
      refunds: (id: string, page: number = 1) =>
        `/orders/${id}/refunds/p/${page}`,
      stateChanges: (id: string, page: number = 1) =>
        `/orders/${id}/state-changes/p/${page}`,
      orderSummary: (id: string) => `/orders/${id}/order-summary`,
      adjustments: (id: string) => `/orders/${id}/adjustments`,
      newAdjustment: (id: string) => `/orders/${id}/adjustments/new`,
      editAdjustment: (id: string, adjustmentId: string) =>
        `/orders/${id}/adjustments/${adjustmentId}`,
      returns: (id: string) => `/orders/${id}/returns`,
      newReturn: (id: string) => `/orders/${id}/returns/new`,
      editReturn: (id: string, returnId: string) =>
        `/orders/${id}/returns/${returnId}`,
      cancelledOrder: (id: string) => `/orders/cancelled-orders/${id}`,
      import: `/orders/import/${IMPORT_TYPE.BULK_ORDER_CREATION}`,
      analytics: {
        base: "/orders/analytics",
        orderAnalytics: (page: number = 1) =>
          `/orders/analytics/order-analytics/p/${page}`,
        passesAnalytics: (page: number = 1) =>
          `/orders/analytics/passes-analytics/p/${page}`,
      },
      notes: (id: string, page: number = 1) => `/orders/${id}/notes/p/${page}`,
    },
  },
  REFUND_REASONS: {
    backendPageNames: ["refund_reasons", "refund-reasons"],
    frontendPageNames: ["refund_reasons", "refund-reasons"],
    component: RefundReasonsComponent,
    route: "/refund-reasons",
    sidebarId: "",
    routeMap: {
      base: "/refund-reasons",
      pagination: (page: number = 1) => `/refund-reasons/p/${page}`,
      details: (id: string) => `/refund-reasons/${id}`,
    },
  },
  PAYMENT_METHODS: {
    backendPageNames: ["payment_methods", "payment-methods"],
    frontendPageNames: ["payment_methods", "payment-methods"],
    component: PaymentMethodsComponent,
    route: "/payment-methods",
    sidebarId: "",
    routeMap: {
      base: "/payment-methods",
      new: "/payment-methods/new",
      edit: (id: string) => `/payment-methods/${id}/edit`,
      pagination: (page: number = 1) => `/payment-methods/p/${page}`,
    },
  },
  COUPON_CATEGORIES: {
    backendPageNames: ["coupon_categories", "coupon-categories"],
    frontendPageNames: ["coupon_categories", "coupon-categories"],
    component: CouponCategoriesComponent,
    route: "/coupon-categories",
    sidebarId: "",
    routeMap: {
      base: "/coupon-categories",
      new: "/coupon-categories/new",
      edit: (id: string) => `/coupon-categories/${id}/edit`,
      pagination: (page: number = 1) => `/coupon-categories/p/${page}`,
    },
  },
  COUPONS: {
    backendPageNames: ["coupons"],
    frontendPageNames: ["coupons"],
    component: CouponsComponent,
    route: "/coupons",
    sidebarId: "",
    routeMap: {
      base: "/coupons",
      new: "/coupons/new/details",
      details: (id: string) => `/coupons/${id}/details`,
      rules: (id: string) => `/coupons/${id}/rules`,
      actions: (id: string) => `/coupons/${id}/actions`,
      pagination: (page: number = 1) => `/coupons/p/${page}`,
      couponOrders: (id: string, page: number = 1) =>
        `/coupons/${id}/coupon-order/p/${page}`,
    },
  },
  RETURN_REASONS: {
    backendPageNames: ["return_reasons", "return-reasons"],
    frontendPageNames: ["return_reasons", "return-reasons"],
    component: ReturnReasonsComponent,
    route: "/return-reasons",
    sidebarId: "",
    routeMap: {
      base: "/return-reasons",
      new: "/return-reasons/new",
      edit: (id: string) => `/return-reasons/${id}/edit`,
      pagination: (page: number = 1) => `/return-reasons/p/${page}`,
    },
  },
  AFFILIATE_LINKS: {
    backendPageNames: ["affiliate_links", "affiliate-links"],
    frontendPageNames: ["affiliate_links", "affiliate-links"],
    component: AffiliateLinksComponent,
    route: "/affiliate-links",
    sidebarId: "",
    routeMap: {
      base: "/affiliate-links",
      new: "/affiliate-links/new",
      edit: (id: string) => `/affiliate-links/${id}/edit`,
      pagination: (page: number = 1) => `/affiliate-links/p/${page}`,
    },
  },
  INVITEES: {
    backendPageNames: ["user_leads", "invitees"],
    frontendPageNames: ["user_leads", "invitees"],
    component: InviteesComponent,
    route: "/invitees",
    sidebarId: "",
    routeMap: {
      base: "/invitees",
      pagination: (page: number = 1) => `/invitees/p/${page}`,
    },
  },
  ADMISSION_SETTINGS: {
    backendPageNames: ["admission_settings"],
    frontendPageNames: ["admission_settings"],
    component: AdmissionSettingsComponent,
    route: "/admission_settings",
    sidebarId: "",
    routeMap: {
      base: "/admission_settings",
    },
  },
  STAFF_ROLES: {
    backendPageNames: ["staff_roles", "staff-roles"],
    frontendPageNames: ["staff_roles", "staff-roles"],
    component: StaffRolesComponent,
    route: "/staff_roles",
    sidebarId: "",
    routeMap: {
      base: "/staff_roles",
      pagination: (page: number) => `/staff_roles/p/${page}`,
      new: "/staff_roles/new",
      edit: (id: string) => `/staff_roles/${id}/edit`,
    },
  },
  ITINERARY: {
    backendPageNames: ["itinerary"],
    frontendPageNames: ["itinerary"],
    component: ItineraryComponent,
    route: "/itinerary",
    sidebarId: "",
    routeMap: {
      base: "/itinerary",
    },
  },

  BASE: {
    backendPageNames: [],
    frontendPageNames: [],
    component: null,
    route: `/workspaces/p/1`,
    sidebarId: "",
    routeMap: {
      base: `/workspaces/p/1`,
    },
  },
  LOGIN: {
    backendPageNames: [],
    frontendPageNames: [],
    component: null,
    route: "/login",
    sidebarId: "",
    routeMap: {
      base: "/login",
      admin: "/login",
    },
  },
  FORGET_PASSWORD: {
    backendPageNames: [],
    frontendPageNames: [],
    component: null,
    route: "/forget-password",
    sidebarId: "",
    routeMap: {
      base: "/forget-password",
    },
  },
  UNAUTHORIZED: {
    backendPageNames: [],
    frontendPageNames: [],
    component: null,
    route: "/unauthorized",
    sidebarId: "",
    routeMap: {
      base: "/unauthorized",
    },
  },
  INTERNAL_ERROR: {
    backendPageNames: [],
    frontendPageNames: [],
    component: null,
    route: "/sorry",
    sidebarId: "",
    routeMap: {
      base: "/sorry",
    },
  },
  ERROR_PAGE: {
    backendPageNames: [],
    frontendPageNames: [],
    component: null,
    route: "/404",
    sidebarId: "",
    routeMap: {
      base: "/404",
    },
  },
  ERROR_BOUNDARY: {
    backendPageNames: [],
    frontendPageNames: [],
    component: null,
    route: "/error",
    sidebarId: "",
    routeMap: {
      base: "/error",
    },
  },
  HOME: {
    backendPageNames: [],
    frontendPageNames: [],
    component: null,
    route: "/",
    sidebarId: "",
    routeMap: {
      base: "/",
    },
  },
  RETURNS: {
    backendPageNames: ["returns"],
    frontendPageNames: ["returns"],
    component: null,
    route: "",
    sidebarId: "",
    routeMap: {},
  },
  PAYMENTS: {
    backendPageNames: ["payments"],
    frontendPageNames: ["payments"],
    component: null,
    route: "",
    sidebarId: "",
    routeMap: {},
  },
  REFUNDS: {
    backendPageNames: ["refunds"],
    frontendPageNames: ["refunds"],
    component: null,
    route: "",
    sidebarId: "",
    routeMap: {},
  },
  ANALYTICS: {
    backendPageNames: ["statistics", "analytics"],
    frontendPageNames: ["statistics", "analytics"],
    component: AnalyticsComponent,
    route: "/analytics",
    sidebarId: "",
    routeMap: {
      base: "/analytics",
      daily: `/analytics/daily`,
      event: `/analytics/event`,
      printing: `/analytics//printing`,
      sessions: `/analytics//sessions`,
      order: `/analytics/order`,
      badge: `/analytics/badge`,
      vip: `/analytics/vip`,
    },
  },
  APP_CONFIG: {
    backendPageNames: ["app-config", "app_config", "app_configs"],
    frontendPageNames: ["app-config", "app_config", "app_configs"],
    component: AppConfigComponent,
    route: "/app_config",
    sidebarId: "",
    routeMap: {
      base: "/app_config",
      capacity_config: {
        base: `/app_config/capacity-config`,
        edit: (id: string) => `/app_config/capacity-config/${id}`,
      },
      event_wrapped: {
        base: `/app_config/event-wrapped`,
        edit: (id: string) => `/app_config/event-wrapped/${id}`,
      },
      event_review_questions: {
        base: `/app_config/event-review-questions`,
      },
      app_builder: {
        base: `/app_config/app-builder`,
        new: `/app_config/app-builder/new`,
        edit: (id) => `/app_config/app-builder/${id}`,
        pagination: (page = 1) => `/app_config/app-builder/p/${page}`,
      },
    },
  },
  ACCESS_CONFIG: {
    backendPageNames: ["access-config", "access_config"],
    frontendPageNames: ["access-config", "access_config"],
    component: AccessConfigComponent,
    route: "/access-config",
    sidebarId: "",
    routeMap: {
      base: "/access-config",
      new: `/access-config/new`,
      pagination: (page: number = 1) => `/access-config/p/${page}`,
      edit: (id: string) => `/access-config/edit/${id}`,
      view: (id: string) => `/access-config/view/${id}`,
    },
  },
  THEMING: {
    backendPageNames: [""],
    frontendPageNames: ["theming"],
    component: ThemingComponent,
    route: "/theming",
    sidebarId: "",
    routeMap: {
      base: "/theming",
    },
  },
  STORE_STATISTICS: {
    backendPageNames: ["store_statistics"],
    frontendPageNames: ["store_statistics"],
    component: BadgeMetrics,
    route: "/store_statistics",
    sidebarId: "",
    routeMap: {
      base: "/store_statistics",
      orderAnalytics: (page: string) =>
        `/store_statistics/order-analytics/p/${page || 1}`,
      passesList: (page: string) =>
        `/store_statistics/passes-list/p/${page || 1}`,
      assignBadge: (id: string) => `/store_statistics/assign-pass/${id}`,
      assignLogs: (id: string, page: string) =>
        `/store_statistics/assign-pass/${id}/logs/p/${page || 1}`,
      transferBadge: (id: string) => `/store_statistics/transfer-pass/${id}`,
      import: () =>
        `/store_statistics/passes-list/import/${IMPORT_TYPE.BULK_ASSIGN_BADGES}`,
    },
  },
  ORDER_STATISTICS: {
    backendPageNames: ["order_statistics", "orders"],
    frontendPageNames: ["order_statistics", "orders"],
    component: OrderMetrics,
    route: "/order_statistics",
    sidebarId: "",
    routeMap: {
      base: "/order_statistics",
      orderAnalytics: (page: string) =>
        `/order_statistics/order-analytics/p/${page || 1}`,
      passesList: (page: string) =>
        `/order_statistics/passes-list/p/${page || 1}`,
    },
  },
  WEB_PAGES: {
    backendPageNames: ["webpages", "web-pages"],
    frontendPageNames: ["webpages", "web-pages"],
    component: WebPages,
    route: "/web-pages",
    sidebarId: "",
    routeMap: {
      base: `/web-pages`,
      pagination: (page) => `/web-pages/p/${page}`,
      new: `/web-pages/new`,
      edit: (id) => `/web-pages/${id}/edit`,
    },
  },
  CUSTOM_REGISTRATION_FORM_FIELDS: {
    backendPageNames: [],
    frontendPageNames: ["custom_registration_form_fields"],
    component: "",
    route: "",
    sidebarId: "",
    routeMap: {},
  },
};

export type RouteKey = keyof typeof ROUTES_LIST;

const ROUTES_LIST = Object.freeze(routesList);

// Export as default object
export { ROUTES_LIST };
